"use client";

import moment from "moment-timezone";
import { Open_Sans, Montserrat, Syncopate } from "next/font/google";
import { usePathname } from "next/navigation";
import "moment/locale/pt-br";
import "shared-components/globals.css";

import ToastProvider from "@/components/ToastProvider";
import { WithStore } from "@/components/WithStore";
import { WithSwrConfig } from "@/components/WithSwrConfig";
import { useApiRouter } from "@/hooks/use-api-router";
import { useAuthCheck } from "@/hooks/use-auth-check";

import "./globals.css";

moment.locale("pt-br");

const mainFont = Open_Sans({
  weight: ["300", "400", "500", "600", "700", "800"],
  subsets: ["latin"],
  variable: "--open-sans",
});

const secondaryFont = Montserrat({
  weight: ["300", "400", "500", "600", "700", "800"],
  subsets: ["latin"],
  variable: "--montserrat",
});

const tertiaryFont = Syncopate({
  weight: ["400", "700"],
  subsets: ["latin"],
  variable: "--syncopate",
});

const imagesToPreload = [
  "/images/home/card_animais.svg",
  "/images/home/card_artes.svg",
  "/images/home/card_atleticomg.svg",
  "/images/home/card_barcelona.svg",
  "/images/home/card_botafogo.svg",
  "/images/home/card_chicagobulls.svg",
  "/images/home/card_cruzeiro.svg",
  "/images/home/card_educacao.svg",
  "/images/home/card_entretenimento.svg",
  "/images/home/card_espiritualidade.svg",
  "/images/home/card_esportes.svg",
  "/images/home/card_estilodevida.svg",
  "/images/home/card_eventosedatas.svg",
  "/images/home/card_flamengo.svg",
  "/images/home/card_fluminense.svg",
  "/images/home/card_hobbies.svg",
  "/images/home/card_natureza.svg",
  "/images/home/card_nba.svg",
  "/images/home/card_profissoes.svg",
  "/images/home/card_psg.svg",
  "/images/home/card_santos.svg",
  "/images/home/card_saopaulo.svg",
  "/images/home/card_temassociais.svg",
  "/images/home/card_times.svg",
  "/images/home/card_vasco.svg",
];

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();

  // Verificar autenticação via cookie na inicialização
  useApiRouter();
  useAuthCheck();

  return (
    <html
      className={`${mainFont.variable} ${secondaryFont.variable} ${tertiaryFont.variable}`}
      lang="pt-BR"
      suppressHydrationWarning
    >
      <head>
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        {pathname === "/" &&
          imagesToPreload.map((path, index) => <link key={index} rel="preload" href={path} as="image" />)}
        <title>Remix</title>
        <meta
          name="description"
          content="Camisetas e produtos personalizados de times, artistas e temas que você ama. Encontre sua identidade na Remix."
        />
        <meta property="og:title" content="Remix" />
        <meta
          property="og:description"
          content="Camisetas e produtos personalizados de times, artistas e temas que você ama. Encontre sua identidade na Remix."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://useremix.com.br" />
      </head>

      <body className="bg-base-white" suppressHydrationWarning>
        <main>
          <WithStore>
            <ToastProvider>
              <WithSwrConfig>{children}</WithSwrConfig>
            </ToastProvider>
          </WithStore>
        </main>
      </body>
    </html>
  );
}
